import { ref, readonly, computed } from 'vue'
import type { IAuthCredentials } from '@/types'

interface AuthState {
  token: string | null
  tenantId: string | null
  userId: string | null
}

export function useAuth() {
  const state = ref<AuthState>({
    token: null,
    tenantId: null,
    userId: null,
  })

  // Computed properties
  const isAuthenticated = computed(() => !!state.value.token)

  // Public methods
  const setAuthCredentials = (credentials: IAuthCredentials): void => {
    state.value.token = credentials.userToken || null
    state.value.tenantId = credentials.tenantId || null
    state.value.userId = credentials.userId || null
  }

  const clearAuth = (): void => {
    state.value.token = null
    state.value.tenantId = null
    state.value.userId = null
  }

  const getAuthHeaders = (): Record<string, string> => {
    const headers: Record<string, string> = {}

    if (state.value.token) {
      headers.Authorization = `Bearer ${state.value.token}`
    }

    if (state.value.tenantId) {
      headers['X-Tenant-ID'] = state.value.tenantId
    }

    if (state.value.userId) {
      headers['X-User-ID'] = state.value.userId
    }

    return headers
  }

  // Return reactive interface
  return {
    // State (readonly)
    isAuthenticated: readonly(isAuthenticated),
    token: readonly(computed(() => state.value.token)),
    tenantId: readonly(computed(() => state.value.tenantId)),
    userId: readonly(computed(() => state.value.userId)),

    // Methods
    setAuthCredentials,
    clearAuth,
    getAuthHeaders,
  }
}

export default useAuth
